import { STORAGE_VALUE_PREFIX } from '@/settings/projectSetting'
import { isUnDef } from './is'

enum ValueType {
  DATE = 'date',
  EXPR = 'expr',
  NUMB = 'numb',
  BOOL = 'bool',
  STRN = 'strn',
  OBJT = 'objt',
}

function prefixValueType(type: ValueType) {
  return `${STORAGE_VALUE_PREFIX}${type}|`
}

const ValueTypeWithPrefix: {
  -readonly [K in keyof typeof ValueType & string as `P_${K}`]: string
} = {
  P_DATE: '',
  P_EXPR: '',
  P_NUMB: '',
  P_BOOL: '',
  P_STRN: '',
  P_OBJT: '',
}

Object.keys(ValueType).forEach((key) => {
  ValueTypeWithPrefix['P_' + key] = prefixValueType(ValueType[key])
})

export function encode(value: any) {
  if (Object.prototype.toString.call(value) === '[object Date]') {
    return ValueTypeWithPrefix.P_DATE + value.toISOString()
  }
  if (Object.prototype.toString.call(value) === '[object RegExp]') {
    return ValueTypeWithPrefix.P_EXPR + value.source
  }
  if (typeof value === 'number') {
    return ValueTypeWithPrefix.P_NUMB + value
  }
  if (typeof value === 'boolean') {
    return ValueTypeWithPrefix.P_BOOL + (value ? '1' : '0')
  }
  if (typeof value === 'string') {
    return ValueTypeWithPrefix.P_STRN + value
  }
  if (typeof value === 'function') {
    return ValueTypeWithPrefix.P_STRN + value.toString()
  }
  if (value === Object(value)) {
    return ValueTypeWithPrefix.P_OBJT + JSON.stringify(value)
  }

  return value
}

export function decode(value: string) {
  if (!value.startsWith(STORAGE_VALUE_PREFIX)) {
    return value
  }
  const typeLen = STORAGE_VALUE_PREFIX.length + 5
  const type = value.substr(0, typeLen)
  const source = value.substring(typeLen)
  switch (type) {
    case ValueTypeWithPrefix.P_DATE:
      return new Date(source)

    case ValueTypeWithPrefix.P_EXPR:
      return new RegExp(source)

    case ValueTypeWithPrefix.P_NUMB:
      return Number(source)

    case ValueTypeWithPrefix.P_BOOL:
      return Boolean(source === '1')

    case ValueTypeWithPrefix.P_STRN:
      return '' + source

    case ValueTypeWithPrefix.P_OBJT:
      return JSON.parse(source)

    default:
      return value
  }
}

type storageType = 'local' | 'session'

/**
 * 本地缓存基础类，代理localStorage或sessionStorage行为
 */
export class BaseStorage {
  webStorage: Storage
  useExpire: boolean
  constructor(type: storageType, useExpire = false) {
    this.webStorage = window[`${type}Storage`]
    this.useExpire = useExpire
  }
  getLength() {
    return this.webStorage.length
  }
  isEmpty() {
    return this.webStorage.length === 0
  }
  get(key: string): any {
    const item = this.webStorage.getItem(key)
    if (!item) {
      return null
    }
    const data = decode(item)
    if (this.useExpire) {
      const { value, expires } = data
      if (isUnDef(expires) || expires >= Date.now()) {
        return value
      }
      this.remove(key)
      return null
    } else {
      return data
    }
  }
  set(key: string, value: any, expires: number | null = null): boolean {
    let data = value
    if (this.useExpire) {
      data = {
        value,
        time: Date.now(),
        expires: !isUnDef(expires) ? Date.now() + expires * 1000 : null,
      }
    }
    try {
      this.webStorage.setItem(key, encode(data))
      return true
    } catch (e) {
      console.log(e)
      return false
    }
  }
  has(key: string) {
    return this.webStorage.getItem(key) !== null
  }
  getKey(index: number) {
    return this.webStorage.key(index)
  }
  getIndex(index: number) {
    const key = this.getKey(index)
    return (key && this.get(key)) || null
  }
  getAll(): Recordable {
    const res = {},
      len = this.webStorage.length
    for (let i = 0; i < len; i++) {
      const k = this.getKey(i)
      if (k) {
        res[k] = this.get(k)
      }
    }
    return res
  }
  getAllKeys(): string[] {
    const keys: string[] = []
    const len = this.webStorage.length
    for (let i = 0; i < len; i++) {
      const k = this.getKey(i)
      k && keys.push(k)
    }
    return keys
  }
  remove(key: string) {
    this.webStorage.removeItem(key)
  }
  clear() {
    this.webStorage.clear()
  }
}
